import Match from "core/src/core/match.js";
import * as random from "core/src/core/util/random.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { hostCadence } from "core/src/ai/crowd.js";
import * as cf from "core/src/core/core-functions.js";
import { SWITCH_ANIM_DURATION } from "core/src/core/constants.js";
import { cloneCellStamps } from "core/src/core/util/cell-stamps.js";
import * as matchStats from "core/src/core/match-stats.js";
import * as respawn from "core/src/core/respawn.js";

// How the authoritative loop is timed. The simulation steps every UPDATE_TICK ms,
// but we only stream a delta to the client every CLIENT_UPDATE_TICK ms (worms move
// along smooth trajectories, so the client interpolates between sparse updates).
const TIME_BETWEEN_GAMES = 5000; // ms paused between rounds of a match
const UPDATE_TICK = 15; // ms between simulation steps (small rosters; the crowd ladder lengthens it for a full box)
const CLIENT_UPDATE_TICK = 45; // ms between state pushes to the client
// Switcharoonie online (client-authoritative worms can't be teleported to new owners mid-flight):
// the host freezes everyone at a scheduled near-future game-time, then WAITS until every client's
// own segment stream confirms its worm actually stopped at the freeze (or a timeout, so one dead
// connection can't hang the round). Only then do the portals open: goTime is scheduled a full
// SWITCH_ANIM_DURATION ahead and broadcast immediately, so every client sees the whole swap
// animation play over an already-frozen world and everyone resumes together at go (swap applied
// at go). Sequence: freeze -> all-confirmed -> animation -> go.
// FREEZE_LEAD is the latency budget: a client whose one-way delay exceeds it learns of the freeze
// after the freeze instant has passed and freezes late/desynced. 0.2s covers most real pings while
// keeping the grab -> everyone-stops gap tight. TODO: make this ADAPTIVE (worst measured RTT in the
// match + margin, so a low-ping lobby gets ~0.1s and a laggy one more) - probably together with
// making the client's render smoothness/delay adaptive, which shares the same latency measurements.
const SWITCH_FREEZE_LEAD = 0.2;      // seconds of game-time between grab and the freeze instant
const SWITCH_CONFIRM_TIMEOUT = 1.5;  // max seconds after freezeTime to wait for every freeze confirmation
const SWITCH_GO_LEAD = 0.25;         // animation start scheduled this far ahead so switch_go outruns transit

// The game state as a client gets it (game_start, and a reconnect's snapshot).
// Left on the host:
//   - playArea.grid: one cell per map pixel (~1M entries on a 1000px map), which
//     the client never renders from (it draws worm trails as vector segments);
//     with it game_start would be multi-megabyte,
//   - each player's aiData (a fill seat's skill and traits: having it at all says
//     which seats are fill seats) and the greed values its AI chases,
//   - the round's seed (whoever holds it can predict every power-up spawn). The
//     client still needs A seed: it runs core's own effect code for its own worm, and
//     the drunk wobble draws from gameState.seed (core random.js throws without a
//     valid one, which killed the client's render loop the moment a drunk landed:
//     the game froze and the silent worm sat there alive). So it gets a fresh one
//     of its own, unrelated to the host's.
export function wireGameState(gameState) {
    const { seed, powerUpGreedValues, ...rest } = gameState; // eslint-disable-line no-unused-vars
    return {
        ...rest,
        seed: random.generateSeed() ^ ((Math.random() * 0x100000000) | 0),
        players: (gameState.players || []).map(({ aiData, ...player }) => player), // eslint-disable-line no-unused-vars
        playArea: gameState.playArea
            ? { rows: gameState.playArea.rows, cols: gameState.playArea.cols, grid: [] }
            : null,
    };
}

// runMatch drives one full authoritative match (a best-of-N sequence of rounds) for
// a single room and pushes every lifecycle + state-delta message through `send`. It
// is transport agnostic: `send(message)` is called with plain objects tagged by
// `type`, so the caller decides how they reach the client (socket.io, TCP, a test
// harness, ...). Returns controls to feed input and to tear the match down.
//
// Messages emitted (each an object with a `type`):
//   match_start    { matchConfig }            once, before the first round
//   game_start     { gameState }              full initial state of each round
//   game_update    { data }                   incremental getGameStateChanges() delta
//   game_over      {}                          the current round ended
//   game_countdown { duration }               pause before the next round
//   match_over     {}                          the match is decided; runner stops
//
// A `director` (optional) turns the fixed match into a changing one (Play Now's
// endless room, the Royale knock-out; see directors/): it may change the roster,
// the map and the power-up rules between rounds, end a round early, reset the
// score and decide when the match is over. Without one, nothing here changes.
//   director.beforeRound({ roundIndex, score })
//       -> null, or { players?, map?, disabledPowerUps?, powerUpSpawnChance?,
//          crowdLevel?, maxScore?, endAtAlive?, resetScore?, setup?,
//          holdMs? (wait this long after round_setup before game_start) }
//       Called right before every round (the first included). Fields given
//       replace the match config's; `setup` rides to clients on round_setup.
//   director.afterRound({ gameState, roundScore, score, roundIndex, left })
//       -> { done?, pauseMs?, result?, next? }
//       Called when a round has been scored. `done` ends the match after the
//       lap; `result` rides on game_over, `next` on game_countdown.
//   director.matchResult({ score })  -> extra fields for match_over (optional)
// With a director two more messages go out:
//   round_setup    { matchConfig, ...setup }   before each game_start
// With or without one, onRound({ gameState, roundStats, roundIndex }) is called
// for every finished round, before afterRound (optional).
export default function runMatch({ matchConfig, send, director = null, onError = null, onRound = null }) {
    // The simulation step and push interval for this roster (crowd ladder):
    // 15ms steps pushing every 45ms; 20ms steps pushing every 40ms for a full box.
    // (let: a director's roster change moves the room along the ladder.)
    let cadence = hostCadence(matchConfig.players.length, matchConfig.crowdLevel);
    let tickMs = cadence.tickMs;
    let pushMs = cadence.pushMs;
    let match = Match({ matchConfig });
    // Rounds played since the runner started (a director's score reset starts a
    // new Match, whose own round count starts over).
    let totalRounds = 0;
    let pauseMs = TIME_BETWEEN_GAMES;
    let lastSetup = null; // the last round_setup sent (a late joiner's snapshot carries it)
    // The update format the room's clients all read (server.js setWire): 2 by default,
    // the compact 3 once every connected client has said it understands it.
    let wire = 2;
    function setWire(v) { wire = v === gsf.WIRE_VERSION_COMPACT ? v : 2; }
    let humanIds = new Set(matchConfig.players.filter((p) => p.type === "human").map((p) => p.id));
    const claimedTwinPus = new Set(); // twin power-up ids already claimed by an accepted client twin (contest guard)
    // Humans who disconnected mid-match. Their worm is externallyControlled, so with no
    // client streaming it, it freezes ALIVE forever (never advances, never self-crashes)
    // - a "ghost" that can win rounds by standing still and, with two+ of them, keeps the
    // round from ever ending (alive count never drops to 1). We keep them dead instead.
    const removedPlayers = new Set();
    // Humans whose socket dropped but whose seat may still be reclaimed (a reload): the
    // sim takes their worm over (externallyControlled off -> core drives it on its last
    // steering), so it keeps playing instead of freezing, and a reconnect hands it back
    // live. No kill timer: a simulated worm crashes on its own; anyone still suspended
    // at the next round start converts to removed (see startGame).
    const suspendedPlayers = new Set();
    // True only while a round is live (between game.start() and gameOver). `game` still
    // points at the finished state during the countdown, so guard input on this instead
    // to keep late worm_segments / worm_crashed from mutating an already-scored round.
    let roundActive = false;
    let lapEndTime = 0; // when > 0 we're in the post-round victory lap; start next round at this time
    let spawnsRead = 0; // how far adoptRespawns has read the round's gameEvents
    let switchSeq = null; // switcharoonie orchestration: { phase: "freezing"|"frozen"|"anim", freezeTime, goTime, assignments }
    // Per worm: the latest segment endTime ACTUALLY adopted from the owning client's stream.
    // This is the switcharoonie freeze confirmation signal - it can't be read off the track's
    // last segment because the tick loop stamps that endTime forward for collision checks.
    const clientSegEnd = new Map();
    // Silent-worm watchdog. A human's worm only moves when its client streams it, so a
    // client that stops streaming (its render loop died, its tab went to the
    // background, its device slept) while the socket stays open leaves the worm
    // standing on the host: alive, unmoving, unkillable, and the round's winner by
    // default. (A closed socket is a different story: suspendPlayer hands the worm to
    // the sim.) lastHeard is the wall time a segment was last adopted per worm;
    // watchFrom is when the worms last became free to move (the start phase or a
    // switcharoonie freeze ended), before which silence means nothing.
    const SILENT_WORM_LIMIT_MS = matchConfig.silentWormLimitMs || 3000;
    const lastHeard = new Map();
    let watchFrom = 0;
    let finalLap = false; // true when the current victory lap is the match's last: end (match_over) instead of a new round
    let game = null;
    let stopped = false;
    let timer = null;
    let clock = {};

    send({ type: "match_start", matchConfig });
    startGame();

    // The director's say before a round: a new roster, map or rules. Applied to the
    // match config in place (core reads it afresh for every round, match.js
    // prepareNextGame), then announced, so clients rebuild what they draw from.
    function directRound() {
        if (!director || !director.beforeRound) return null;
        const d = director.beforeRound({ roundIndex: totalRounds, score: match.getCurrentScore() }) || {};
        if (d.players) {
            matchConfig.players = d.players;
            humanIds = new Set(d.players.filter((p) => p.type === "human").map((p) => p.id));
            // Whoever is no longer seated has nothing left to be kept dead for.
            for (const pid of [...removedPlayers]) if (!humanIds.has(pid)) removedPlayers.delete(pid);
            for (const pid of [...suspendedPlayers]) if (!humanIds.has(pid)) suspendedPlayers.delete(pid);
        }
        if (d.map) matchConfig.map = d.map;
        if (d.disabledPowerUps !== undefined) matchConfig.disabledPowerUps = d.disabledPowerUps;
        if (d.powerUpSpawnChance !== undefined) matchConfig.powerUpSpawnChance = d.powerUpSpawnChance;
        if (d.crowdLevel !== undefined) matchConfig.crowdLevel = d.crowdLevel;
        if (d.maxScore !== undefined) matchConfig.maxScore = d.maxScore;
        if (d.respawn !== undefined) matchConfig.respawn = d.respawn; // Respawn rounds (core respawn.js), or null
        if (d.players || d.crowdLevel !== undefined) {
            cadence = hostCadence(matchConfig.players.length, matchConfig.crowdLevel);
            tickMs = cadence.tickMs;
            pushMs = cadence.pushMs;
        }
        // A fresh score sheet (a new Play Now set, the Royale final): a new Match
        // over the same config, which also lets go of the finished rounds.
        if (d.resetScore) match = Match({ matchConfig });
        lastSetup = { type: "round_setup", matchConfig, round: totalRounds + 1, scoreReset: !!d.resetScore, ...(d.setup || {}) };
        send(lastSetup);
        return d;
    }

    function startGame() {
        if (stopped) return;
        const directed = directRound();
        claimedTwinPus.clear(); // power-up ids consumed by an accepted client twin, this round
        switchSeq = null; // no switcharoonie in progress at the start of a round
        clientSegEnd.clear();
        lastHeard.clear();
        watchFrom = 0;
        spawnsRead = 0;
        const seed = random.generateSeed();
        game = match.prepareNextGame(seed);
        // This host broadcasts to clients: every switcharoonie is run by handleWormSwitch
        // (freeze, go, swap, with the messages the clients animate from), whoever has
        // worms in the round. See core activatePowerUp: never inferred from the roster.
        game.gameState.hostDrivenSwitch = true;
        // Human worms are authoritative on their own client; we adopt the path they
        // stream (applyClientSegments) instead of simulating their movement.
        game.gameState.worms.forEach((w) => {
            if (humanIds.has(w.playerId)) w.externallyControlled = true;
        });
        // A suspended player's worm keeps driving until it crashes on its own - no kill
        // timer mid-round. But anyone STILL suspended when a new round starts didn't
        // make it back: convert to removed so they don't respawn as an unmanned worm
        // round after round. A later reconnect still reclaims the seat (reattach lifts
        // the mark) and they play from the following round.
        suspendedPlayers.forEach((pid) => removedPlayers.add(pid));
        suspendedPlayers.clear();
        // A player who left mid-match doesn't respawn as a fresh ghost each round: kill
        // their worm at the start so the round settles among the players still here.
        game.gameState.worms.forEach((w) => {
            if (removedPlayers.has(w.playerId) && w.alive) {
                respawn.holdOut(game.gameState, w.playerId, true); // (a Respawn round would put them back in)
                cf.killWorm(game.gameState, w.id);
            }
        });
        // Effects disabled in online play (client-authoritative worms can't faithfully mirror
        // them). Empty since switcharoonie (wormSwitch) went online-capable: the host runs a
        // freeze -> swap -> go sequence and clients re-bind (see handleWormSwitch / the client's
        // prediction.ts switchState). ("clear" is removed from the game entirely, weight 0, so it
        // can't spawn regardless.) The debug harness passes its own list to test subsets.
        game.gameState.disabledEffectTypes = matchConfig.disabledEffectTypes || [];
        // A knock-out round ends once this many are left (core game.js).
        if (directed && typeof directed.endAtAlive === "number") game.gameState.endAtAlive = directed.endAtAlive;
        // Perf bench only (server.js, PERF_BENCH): nobody can crash, so a round runs until
        // the bench stops it and trails keep growing. Rides to clients in game_start.
        if (matchConfig.benchNoCrash) game.gameState.noCrash = true;
        // Debug/test only: seed effects on every worm at round start (matchConfig.debugStartEffects
        // = [{ type, strength, seconds }]), e.g. everyone fat for 30s to test twin-while-fat in
        // /debug/net. The spawn events ride along in the game_start snapshot; each client's own worm
        // picks the effect up (its prediction reads getWormRadius), so it renders + streams resized.
        (matchConfig.debugStartEffects || []).forEach((spec) => {
            game.gameState.worms.forEach((w) => {
                if (!w.alive) return;
                gsf.addEffect(game.gameState, {
                    type: spec.type, strength: spec.strength,
                    duration: spec.seconds, timeLeft: spec.seconds,
                    name: spec.type, time: game.gameState.gameTime, wormId: w.id,
                });
            });
        });
        // The director may hold the round back a moment after announcing it (the
        // Royale's opening, where everyone is introduced before the arena shows):
        // the game is ready but nothing is sent or simulated until then.
        const holdMs = directed && directed.holdMs > 0 ? directed.holdMs : 0;
        if (holdMs) { timer = setTimeout(beginGame, holdMs); return; }
        beginGame();
    }

    function beginGame() {
        if (stopped) return;
        // (the opening is over: whoever arrives from here on is not shown it)
        if (lastSetup && lastSetup.openingMs) lastSetup = { ...lastSetup, openingMs: 0 };
        send({ type: "game_start", gameState: wireGameState(game.gameState) });
        const now = Date.now();
        clock = {
            previousUpdateTime: now,
            // Backdate so the first tick always produces a client update.
            previousClientSendTime: now - 2 * CLIENT_UPDATE_TICK,
            nextUpdateTime: now + tickMs,
            // Event arrays are streamed by INDEX, not by a game-time filter: a worm
            // can die BETWEEN ticks (an adopted client segment crossing a trail),
            // stamping its player_died with the last tick's game-time == the last
            // send's game-time, which a `time >` filter would silently drop. Start at
            // the count already in the game_start snapshot so we only send new ones.
            sentEvents: game.gameState.gameEvents.length,
            sentPowerUpEvents: game.gameState.powerUpEvents.length,
            sentEffectEvents: game.gameState.effectEvents.length,
            // Per-track "how many segments the client already has" cursor, for streaming
            // worm-path segments by INDEX instead of by an endTime filter (see updateGame).
            sentSegments: {},
            // Per-track fingerprint of the last segment as last sent (see streamDelta).
            sentSigs: {},
        };
        game.start();
        roundActive = true;
        timer = setTimeout(updateGame, tickMs);
    }

    // Switcharoonie orchestration. Core defers a client-authoritative switch by setting
    // gameState.pendingWormSwitch (it can't teleport client-owned worms to new owners mid-flight);
    // we drive the freeze -> swap -> go sequence and broadcast the two coordinating messages. The
    // actual per-worm freeze/resume is the clients' job (they own movement); the host owns the
    // timing and the authoritative owner swap on the frozen state.
    function handleWormSwitch() {
        const gs = game.gameState;
        // Debug: force a switcharoonie at a chosen game-time (matchConfig.debugForceSwitchAt), so it
        // can be tested in /debug/net without waiting to randomly grab one. Fires once per round.
        if (matchConfig.debugForceSwitchAt != null && !switchSeq && !gs._debugSwitchDone && gs.gameTime >= matchConfig.debugForceSwitchAt) {
            gs._debugSwitchDone = true;
            gs.pendingWormSwitch = { grabTime: gs.gameTime };
        }
        if (gs.pendingWormSwitch && !switchSeq) {
            gs.pendingWormSwitch = null;
            // Seeded shuffle: computed ONCE here and shipped to every client (they don't recompute).
            const assignments = cf.computeWormSwitchAssignments(gs);
            if (assignments) {
                const freezeTime = gs.gameTime + SWITCH_FREEZE_LEAD;
                // goTime stays null until every client has confirmed its freeze (below); clients
                // hold frozen with NO portal animation until switch_go names the go moment.
                switchSeq = { phase: "freezing", freezeTime, goTime: null, assignments };
                // Host-simulated worms (bots) freeze via the engine: updateWorms stands a worm
                // still once gameTime passes switchFreezeAt (client-owned worms freeze on their
                // own clients). Cleared at go.
                gs.switchFreezeAt = freezeTime;
                // For the debug harness's server view: the same swap-animation info the clients get.
                gs.switchAnim = { freezeTime, goTime: null, assignments };
                send({ type: "switch_freeze", atTime: freezeTime, assignments });
            }
        }
        if (!switchSeq) { gs._switchPhase = null; return; }
        if (switchSeq.phase === "freezing" && gs.gameTime >= switchSeq.freezeTime) {
            switchSeq.phase = "frozen"; // host-side world is frozen; now wait for the clients to confirm
        }
        if (switchSeq.phase === "frozen") {
            // A client's freeze confirmation is its own segment stream: a client-owned worm has
            // provably stopped once its adopted track reaches the freeze instant (the last predicted
            // segment ends at freezeTime, minus at most one prediction step + send batching). Host-
            // simulated worms (bots) froze exactly at freezeTime in the engine, so only
            // externallyControlled worms are waited on. Timeout so one dead connection can't hang
            // the round; a client that late-froze past freezeTime also counts (endTime beyond it).
            const EPS = 0.08; // one client prediction step (0.025) + wire quantization, with margin
            const allConfirmed = gs.worms.every((w) => {
                if (!w.alive || !w.externallyControlled) return true;
                return (clientSegEnd.get(w.id) || 0) >= switchSeq.freezeTime - EPS;
            });
            if (allConfirmed || gs.gameTime >= switchSeq.freezeTime + SWITCH_CONFIRM_TIMEOUT) {
                // Everyone is provably frozen: open the portals. goTime is broadcast a full
                // animation ahead of time so every client plays the whole sequence and resumes
                // together; the animation window on every view is [goTime - SWITCH_ANIM_DURATION,
                // goTime] (see the client's switch-anim-renderer).
                switchSeq.phase = "anim";
                switchSeq.goTime = gs.gameTime + SWITCH_GO_LEAD + SWITCH_ANIM_DURATION;
                gs.switchAnim.goTime = switchSeq.goTime;
                send({ type: "switch_go", atTime: switchSeq.goTime });
            }
        }
        if (switchSeq.phase === "anim" && gs.gameTime >= switchSeq.goTime) {
            // Everything happens at GO, together: the authoritative owner swap + switch_slow. The
            // clients already know this moment (switch_go was sent ahead) and swap + re-bind at the
            // same game-time, so switch_slow ramps from the real resume everywhere.
            cf.applyWormSwitch(gs, switchSeq.assignments);
            // The swap can hand a worm between a HUMAN and a HOST-SIMULATED player (bot):
            // recompute who drives each worm from its NEW owner, or the host would keep
            // simulating a worm a client now streams (double-drive) / leave a bot's new worm
            // parked forever waiting for client segments that never come.
            gs.worms.forEach((w) => { w.externallyControlled = humanIds.has(w.playerId); });
            gs.switchFreezeAt = null; // bots resume with everyone else
            // NB: we deliberately do NOT force-resync the swapped worms' tracks here. The server has no
            // business rewriting a client's already-rendered old segments (a client keeps its own finer
            // predicted trail for a worm it lost; ours is just a coarser copy of the same path). Clients
            // reconcile by APPENDING our new segments by time (see remote-game applyUpdate), so old
            // segments are never touched. Colour turns over naturally via each new segment's playerId.
            switchSeq = null;
        }
        gs._switchPhase = switchSeq ? switchSeq.phase : null; // debug view: expose the host's switch phase
    }

    // Build + send the incremental game_update delta. Both the game/power-up/effect
    // event arrays AND the worm-path segments are streamed by INDEX (append-only, always
    // resending the mutable last segment), never by an endTime/time filter: we stamp a
    // client-controlled worm's last-segment endTime forward each tick for collision, so
    // per-track endTimes are non-monotonic - a filter would drop a between-tick death
    // event or skip a late-adopted segment, desyncing the client (a segment skip makes a
    // worm vanish on other screens until the stream re-aligns). Shared by the live round
    // and the post-round victory lap.
    function streamDelta(nowMs) {
        const gs = game.gameState;
        // Built straight from the index cursors below. (This used to start from
        // gsf.getGameStateChanges, whose time-filtered segments and events were then
        // all overwritten here: it scanned every track from its first segment and
        // every event list on each push, a cost that grew with the round.)
        const data = { gameTime: gs.gameTime };
        // Tell the client this frame is the cosmetic victory lap, so a lap crash is not
        // applied to the scoreboard (the winner shouldn't be marked "out" for a lap wipe).
        data.victoryLap = !!gs.victoryLap;
        data.gameEvents = gs.gameEvents.slice(clock.sentEvents);
        // Death forensics for the reload investigation: log every HUMAN worm death
        // with the full kill context (cause, killer, fatal cell + immunity stamps,
        // segment timing, suspended-or-not) so `wrangler tail` shows what killed it.
        for (const ev of data.gameEvents) {
            if (ev.type === "worm_died" && humanIds.has(ev.playerId)) {
                try {
                    console.log("[death] " + JSON.stringify({
                        t: Math.round(ev.time * 1000) / 1000,
                        playerId: ev.playerId, wormId: ev.wormId,
                        cause: ev.cause || "unattributed", via: ev.via || "server",
                        killer: ev.killerTrackId || null,
                        suspended: suspendedPlayers.has(ev.playerId),
                        debug: ev.debug || null,
                    }));
                } catch { /* log only */ }
            }
        }
        data.powerUpEvents = gs.powerUpEvents.slice(clock.sentPowerUpEvents);
        data.effectEvents = gs.effectEvents.slice(clock.sentEffectEvents);
        clock.sentEvents = gs.gameEvents.length;
        clock.sentPowerUpEvents = gs.powerUpEvents.length;
        clock.sentEffectEvents = gs.effectEvents.length;
        const segDelta = {};
        for (const trackId of Object.keys(gs.wormPathSegments)) {
            const segs = gs.wormPathSegments[trackId];
            if (!segs || !segs.length) continue;
            // Skip a track whose tail hasn't changed since the last push: a dead worm, a
            // finished wall-hack ghost track ("<track>#N"), a human between segment
            // batches. Resending its unchanged last segment on every push made each
            // update grow with the number of tracks ever drawn (bandwidth, server
            // stringify, and client decode for nothing). A track's last segment only
            // changes by growing in place (duration) or by a new segment (length); the
            // endTime we stamp forward on human worms is not part of it on purpose:
            // clients rebuild endTime from startTime + duration.
            const last = segs[segs.length - 1];
            const sig = segs.length + ":" + last.duration + ":" + last.startTime + ":" + last.size + ":" + last.playerId + ":" + last.type + ":" + !!last.jump;
            if (clock.sentSegments[trackId] !== undefined && clock.sentSigs[trackId] === sig) continue;
            clock.sentSigs[trackId] = sig;
            // First send for a track: resync from 0 (the client only has the game_start
            // spawn segments). After that: from the cursor, but never past the last index,
            // so the mutable last segment (a bot's grows in place) is resent whenever
            // it changed.
            let from = clock.sentSegments[trackId];
            if (from === undefined) from = 0;
            if (from > segs.length - 1) from = segs.length - 1;
            segDelta[trackId] = { fromIndex: from, segments: segs.slice(from) };
            clock.sentSegments[trackId] = segs.length - 1;
        }
        data.wormPathSegments = segDelta;
        data.perf = { ticks: perf.ticks };
        perf.ticks = 0;
        // The host's send time. NB: on a Durable Object the clock inside a timer
        // callback is the scheduled time, not the wall time, so these stamps read
        // an even cadence whatever the host is doing; the client measures pace and
        // spacing from arrivals instead.
        data.sentAt = Date.now();
        send({ type: "game_update", data: gsf.encodeGameStateChanges(data, wire) });
        clock.previousClientSendTime = nowMs;
    }

    // Ticks simulated since the last client push, shipped on every game_update
    // as `perf.ticks`: the client's netstats overlay (F9) derives the host's pace
    // from it (ticks per wall second, game time per wall second). Measured on
    // the client on purpose: a Durable Object's clock stands still while it runs
    // CPU-bound code, so timing the simulation here reads 0 however slow it is.
    const perf = { ticks: 0 };
    // A tick that throws leaves the match standing (no next tick is scheduled): say
    // so to whoever runs us (the room reports it), then let it surface as before.
    function updateGame() {
        try { stepGame(); } catch (e) { try { if (onError) onError(e, { where: "match tick" }); } catch { /* reporting only */ } throw e; }
    }
    function stepGame() {
        if (stopped || !game) return;
        const updateStartTime = Date.now();
        if (game.isActive()) {
            const deltaTime = (updateStartTime - clock.previousUpdateTime) / 1000;
            // Collision only checks a worm whose latest segment ends at the current
            // game time. Client-authoritative worms are advanced by adopted segments
            // whose times don't line up with our ticks, so stamp their latest segment
            // to this tick's post-update time so they're collision-checked.
            const targetGameTime = game.gameState.gameTime + deltaTime;
            game.gameState.worms.forEach((w) => {
                if (w.externallyControlled && w.alive) {
                    const segs = game.gameState.wormPathSegments[w.trackId];
                    if (segs && segs.length) segs[segs.length - 1].endTime = targetGameTime;
                }
            });
            game.update(deltaTime);
            perf.ticks++;
            adoptRespawns();
            killSilentWorms();
            handleWormSwitch();
            // A human's twin is no longer spawned here: core skips it for externally-controlled
            // (client-authoritative) worms, and the owning client registers its own twin via a
            // "worm_spawn" message (spawnClientWorm) with the id + spawn point + immunity it
            // chose. Bots' twins are still spawned by core and simulated by the host as usual.
            // Throttle client pushes to CLIENT_UPDATE_TICK, but always send the final
            // frame of a round so the client sees the crash / finish.
            if (updateStartTime - clock.previousClientSendTime >= pushMs - 1 || !game.isActive()) {
                streamDelta(updateStartTime);
            }
        }
        clock.previousUpdateTime = updateStartTime;

        // The victory lap runs on THIS same loop (core "victory mode": update() keeps
        // simulating, just never re-ends the round), so a human winner's lap is simulated
        // on the host - their pickups activate (twin etc.) and stream to everyone. End the
        // lap after the countdown by starting the next round.
        if (lapEndTime && updateStartTime >= lapEndTime) {
            lapEndTime = 0;
            afterPause();
            return;
        }
        // Round just ended (update() stopped it): score it, then either finish the match
        // or roll into the victory lap - which re-activates the sim, so the scheduling
        // below keeps ticking straight through it.
        if (!game.isActive()) {
            gameOver();
        }
        if (game.isActive()) {
            // Schedule the next tick on a fixed cadence, skipping past any ticks we
            // already missed (e.g. the event loop stalled) so time doesn't drift.
            clock.nextUpdateTime += tickMs;
            const currentTime = Date.now();
            while (currentTime >= clock.nextUpdateTime) {
                clock.nextUpdateTime += tickMs;
            }
            timer = setTimeout(updateGame, clock.nextUpdateTime - currentTime);
        }
        // else: the match is over (stop() was called) - no further ticks.
    }

    // The pause after a round is over: the next round, or the match's end.
    function afterPause() {
        if (stopped) return;
        if (finalLap) {
            // The last round's lap is over: end the match now (clients have shown
            // the result + countdown, like local play) instead of a new round.
            // `left`: humans who walked out (removed after a missed round start, or
            // still gone at the end). Leaving counts as finishing last: the rating
            // and the results screen both rank them below everyone who stayed.
            const score = match.getCurrentScore();
            send({ type: "match_over", score, stats: match.getMatchStats(), left: [...new Set([...removedPlayers, ...suspendedPlayers])], ...((director && director.matchResult && director.matchResult({ score })) || {}) });
            stop();
        } else {
            startGame();
        }
    }

    function gameOver() {
        if (stopped || !game) return;
        roundActive = false;
        match.addFinishedGameState(game.gameState);
        totalRounds++;
        // Every finished round, before the director moves anyone out: the worm
        // level and the daily challenges (server.js progressRound).
        if (onRound) {
            try {
                const rounds = match.matchState.roundsData;
                onRound({ gameState: game.gameState, roundStats: rounds[rounds.length - 1].roundStats, roundIndex: totalRounds - 1 });
            } catch (e) {
                if (onError) onError(e, { where: "onRound" });
            }
        }
        // A director decides what the round meant (who is out, whether the match is
        // over) and what comes next; without one the match's own score rule does.
        let directed = null;
        if (director && director.afterRound) {
            const rounds = match.matchState.roundsData;
            directed = director.afterRound({
                gameState: game.gameState, roundScore: rounds[rounds.length - 1].roundScore, score: match.getCurrentScore(),
                roundIndex: totalRounds - 1, left: [...new Set([...removedPlayers, ...suspendedPlayers])],
                // the fun stats of the last `n` rounds (a Play Now set's, for its results)
                statsOfLast: (n) => matchStats.aggregateMatchStats(rounds.slice(-n).map((r) => r.roundStats)),
            }) || {};
        }
        pauseMs = directed && typeof directed.pauseMs === "number" ? directed.pauseMs : TIME_BETWEEN_GAMES;
        // Include the authoritative accumulated score (through this finished round)
        // so clients show the settled standings instead of recomputing from their
        // render-delayed / still-propagating view of the death events (which snaps).
        send({ type: "game_over", score: match.getCurrentScore(), ...((directed && directed.result) || {}) });
        // Both the between-rounds pause AND the final results run a victory lap +
        // countdown; the only difference is what happens when it ends (next round vs
        // match_over, handled at lapEndTime in updateGame). This keeps online lingering
        // on the last round's result + winner like local play, instead of snapping to
        // the lobby. The client shows "Final results in N" when `final` is set.
        finalLap = directed ? !!directed.done : match.isMatchOver();
        send({ type: "game_countdown", duration: pauseMs, final: finalLap, ...(directed && directed.next ? { next: directed.next } : {}) });
        // Roll into the victory lap: keep the sim RUNNING (core victory mode) on the
        // main loop so the last worm(s) - bot or human - keep driving, still pick up
        // power-ups and can crash, cosmetically. Bots are AI-steered; a human winner's
        // lap is adopted from their stream (applyClientSegments / setSteering below stay
        // open during the lap). Nothing here changes the locked score. Core pushes the
        // victory_lap game event (with the winner) itself, streamed like any event.
        // A Respawn round (core respawn.js) ends on its timer with everyone still in:
        // nobody to take a lap, the board just stops for the pause. With the sim
        // stopped the tick loop does too, so the pause runs on a timer of its own.
        if (game.gameState.respawn) {
            lapEndTime = 0;
            timer = setTimeout(() => { try { afterPause(); } catch (e) { if (onError) onError(e, { where: "after pause" }); } }, pauseMs);
            return;
        }
        game.enterVictoryLap();
        lapEndTime = Date.now() + pauseMs;
    }

    // Whether input/segments should be accepted right now: during a live round, or during
    // the cosmetic victory lap (so a human winner's lap is simulated + streamed to all).
    function acceptingInput() {
        return !!game && (roundActive || game.gameState.victoryLap);
    }

    // Apply a steering input (-1 left, 0 straight, 1 right) for a player. Ignored between
    // rounds (no active game / countdown before the lap).
    function setSteering(playerId, steering) {
        // (Only for someone in this round: a spectator, or a player knocked out of
        // a Royale, still has a connection that may send keys.)
        if (acceptingInput() && humanIds.has(playerId)) {
            gsf.setPlayerSteering(game.gameState, playerId, steering);
        }
    }

    // Register a client-authoritative twin worm the owning client spawned (a human grabbing a
    // twin power-up). The client picks the id + spawn point and computes the mutual immunity
    // effects; we adopt that worm as-is (externallyControlled) so it streams to everyone and
    // scores, without the host spawning its own diverging copy. Returns whether the client
    // should keep it: { ok } accepted, { rejected } lost a contested grab (roll it back).
    function spawnClientWorm(playerId, reg) {
        if (!acceptingInput() || !humanIds.has(playerId) || !reg || reg.wormId == null) return { ok: false };
        const gs = game.gameState;
        if (gs.worms.some((w) => w.id === reg.wormId)) return { ok: true }; // duplicate message; idempotent
        // Clone the original's distance bookkeeping so the twin's spawn-overlap immunity matches
        // core's twin.activate - so it must exist and belong to this player.
        const orig = gs.worms.find((w) => w.id === reg.originalWormId && w.playerId === playerId);
        if (!orig || !orig.alive) return { ok: false };
        // A twin power-up spawns exactly ONE twin. First registration for it wins; a later one
        // (another client that also predicted grabbing the same power-up) is rejected.
        if (reg.puId != null) {
            if (claimedTwinPus.has(reg.puId)) return { ok: false, rejected: true };
            claimedTwinPus.add(reg.puId);
        }
        const sp = reg.spawn || {};
        const twin = gsf.createWorm(gs, {
            id: reg.wormId,
            playerId,
            direction: typeof sp.direction === "number" ? sp.direction : orig.direction,
            centerX: typeof sp.centerX === "number" ? sp.centerX : orig.centerX,
            centerY: typeof sp.centerY === "number" ? sp.centerY : orig.centerY,
            // BASE radius = the original's base radius, NOT sp.size (the client's spawn size, which is
            // already effect-scaled e.g. 2x when fat). Storing the fat size as the base made any
            // server-computed size double-apply the fat: getWormRadius = base(2x) * fat(2x) = 4x. It
            // only showed at death, since a live twin renders from the client's adopted segments, but
            // the server builds the worm_died segment itself with getWormRadius -> the head ballooned.
            radius: orig.radius,
            distanceTravelled: orig.distanceTravelled,
            distanceTravelledFromCells: cloneCellStamps(orig.distanceTravelledFromCells),
        });
        twin.externallyControlled = true;
        lastHeard.set(twin.id, Date.now()); // its first segments are on their way
        gsf.addWorm(gs, twin);
        // Discard the seed segment addWorm placed: the client streams the twin's whole path from
        // that same point, so an unowned seed would just leave a stub its path jumps away from.
        gs.wormPathSegments[twin.trackId] = [];
        // Apply the client-authoritative effects it shipped for its twin: the mutual "twin" immunity
        // (so our collision won't kill the fresh twin overlapping the original's trail) AND the
        // effects it inherited from the original (wall-hack, speed, ...) so the twin keeps its buffs.
        // The client authors its whole twin (green, client-authoritative); we don't re-derive from
        // our own worm. Scoped to THIS player's twin + original so a client can't buff/curse anyone
        // else's worm. Trusted pre-launch; TODO validate the player actually grabbed a twin power-up.
        (reg.effects || []).forEach((e) => {
            if (!e || (e.wormId !== reg.wormId && e.wormId !== reg.originalWormId)) return;
            if (!gs.worms.some((w) => w.id === e.wormId)) return;
            gsf.addEffect(gs, { ...e });
        });
        return { ok: true };
    }

    // A Respawn round (core respawn.js): core put a player back in with a new worm.
    // A human's is theirs to steer, like their first: its path comes from their
    // stream (the worm_spawned event tells their client where it is), so the seed
    // segment core laid goes, its holing is theirs (seg.jump), and the silent
    // watchdog gives their first segments time to arrive.
    function adoptRespawns() {
        const gs = game.gameState;
        if (!gs.respawn) return;
        const ev = gs.gameEvents;
        for (; spawnsRead < ev.length; spawnsRead++) {
            const e = ev[spawnsRead];
            if (!e || e.type !== "worm_spawned" || !humanIds.has(e.playerId)) continue;
            const w = gs.worms.find((x) => x.id === e.wormId);
            if (!w || !w.alive) continue;
            w.externallyControlled = true;
            w.jump.remainingJumpTime = 0;
            lastHeard.set(w.id, Date.now());
            gs.wormPathSegments[w.trackId] = [];
        }
    }

    // Someone arriving at a running Respawn round (Play Now) plays at once: seated in
    // the match and put in like a player back from a crash (their client gets the
    // worm_spawned event like anyone's). False when they have to wait for the next
    // round (between rounds, or a round that ends on who is left).
    function joinRound(players) {
        if (!game || !roundActive || !game.gameState.respawn || respawn.isTimeUp(game.gameState)) return false;
        const gs = game.gameState;
        for (const p of players) {
            if (humanIds.has(p.id)) continue;
            matchConfig.players = matchConfig.players.concat([p]);
            humanIds.add(p.id);
            if (!gs.players.some((x) => x.id === p.id)) gsf.addPlayer(gs, { id: p.id, clientId: undefined });
            gs.respawn.due[p.id] = gs.gameTime;
        }
        return true;
    }

    // See lastHeard above. Live rounds only (a silent winner on the cosmetic lap harms
    // nobody), and only while worms are free to move.
    function killSilentWorms() {
        const gs = game.gameState;
        if (!roundActive || switchSeq || gs.gameTime <= gs.startPhaseTime) { watchFrom = 0; return; }
        const now = Date.now();
        if (!watchFrom) watchFrom = now;
        gs.worms.forEach((w) => {
            if (!w.alive || !w.externallyControlled) return;
            const silentFor = now - Math.max(watchFrom, lastHeard.get(w.id) || 0);
            if (silentFor > SILENT_WORM_LIMIT_MS) {
                console.log("silent worm killed: " + w.id + " of " + w.playerId + " after " + silentFor + "ms without a segment");
                cf.killWorm(gs, w.id, { via: "silent_client" });
            }
        });
    }

    // Adopt a batch of client-computed path segments for a human's own worm: the
    // client is authoritative for its movement; we validate (finite, bounded
    // duration, no teleport / speed beyond the worm's limit) and paint them into the
    // collision grid via the engine. Collision/death stays server-authoritative.
    function applyClientSegments(playerId, segments) {
        if (!acceptingInput() || !humanIds.has(playerId) || !Array.isArray(segments)) return;
        for (const raw of segments) {
            // Route by the segment's own wormId: a player can control more than one worm
            // (twin), so we can't assume their first. The worm must exist and belong to
            // this player (can't drive someone else's).
            const worm = game.gameState.worms.find((w) => w.id === raw.wormId && w.playerId === playerId);
            if (!worm || !worm.alive) continue;
            // A twin is created mid-round and isn't marked externallyControlled at
            // game_start; once its owner streams segments for it, adopt it (stop
            // server-simulating it, like the original).
            if (!worm.externallyControlled) worm.externallyControlled = true;
            const maxSpeed = worm.speed * 3; // generous headroom for speed power-ups
            if (!isSaneSegment(raw, worm, maxSpeed)) continue;
            // Continuity guard: an adopted segment must continue from where the worm
            // actually is (the previous adopted end). A segment that teleports - e.g.
            // a reconnecting client replaying stale pre-disconnect backlog - would
            // repaint old trail, drag the worm backwards and double-count its
            // distance bookkeeping. Wall-hack is exempt (its wrap legitimately jumps
            // the head across the map). Tolerance is generous vs float noise but far
            // below any real teleport.
            if (!gsf.hasWormEffect(game.gameState, worm.id, "wallHack")) {
                const gap = Math.hypot(raw.startX - worm.centerX, raw.startY - worm.centerY);
                if (gap > worm.radius * 2 + raw.speed * raw.duration + 10) {
                    console.log("[seg-reject] " + playerId + " gap=" + Math.round(gap) + " segStart=(" + Math.round(raw.startX) + "," + Math.round(raw.startY) + ") t=" + raw.startTime + " worm=(" + Math.round(worm.centerX) + "," + Math.round(worm.centerY) + ")");
                    continue;
                }
            }
            // Accept the client's size (power-ups change it) but clamp to a sane range.
            const size = Math.max(worm.radius * 0.4, Math.min(raw.size || worm.radius, worm.radius * 2.5));
            const speed = Math.max(0, Math.min(raw.speed, maxSpeed));
            // playerId: the current owner (segment colour); the track key stays worm.trackId.
            const seg = adoptedSegment(raw, size, speed, worm.playerId, worm.id, worm.trackId);
            cf.applyExternalWormSegment(game.gameState, worm.id, seg);
            // Freeze-confirmation bookkeeping (see clientSegEnd): the client has provably
            // simulated this worm up to seg.endTime.
            const prevEnd = clientSegEnd.get(worm.id) || 0;
            if (seg.endTime > prevEnd) clientSegEnd.set(worm.id, seg.endTime);
            lastHeard.set(worm.id, Date.now());
        }
    }

    // The authoritative copy of a client's segment. Built as a fixed-shape literal in
    // the order the engine lays the fields out (trajectory-util), so the stream sends
    // the same bytes as the client's own segment. That also leaves out the client's
    // private markers (_own, _wrapNext): they must not survive into the authoritative
    // track, or a reconnect snapshot hands them back and the fresh predictor's drain
    // re-streams the whole backlog as if it had just laid it (the reload replay-death
    // bug). A literal matters with 20 players: tracks hold ~50k segments a minute,
    // and a copy made by spread + delete (or by adding fields one at a time) becomes a
    // slow dictionary-mode object, ~1.6KB each instead of ~0.45KB, and 4x slower to make.
    function adoptedSegment(raw, size, speed, playerId, wormId, trackId) {
        const jump = !!raw.jump;
        if (raw.type === "arc") {
            return {
                duration: raw.duration, startX: raw.startX, startY: raw.startY, startDirection: raw.startDirection,
                speed, turningVelocity: raw.turningVelocity, type: raw.type,
                arcCenterX: raw.arcCenterX, arcCenterY: raw.arcCenterY, arcRadius: raw.arcRadius,
                arcStartAngle: raw.arcStartAngle, arcAngleDiff: raw.arcAngleDiff, arcEndAngle: raw.arcEndAngle,
                endX: raw.endX, endY: raw.endY, endDirection: raw.endDirection, startTime: raw.startTime, endTime: raw.endTime,
                size, jump, playerId, wormId, id: trackId,
            };
        }
        if (raw.type === "straight" || raw.type === "still_arc") {
            return {
                duration: raw.duration, startX: raw.startX, startY: raw.startY, startDirection: raw.startDirection,
                speed, turningVelocity: raw.turningVelocity, type: raw.type,
                endX: raw.endX, endY: raw.endY, endDirection: raw.endDirection, startTime: raw.startTime, endTime: raw.endTime,
                size, jump, playerId, wormId, id: trackId,
            };
        }
        // Anything else (rare): a plain copy minus the markers.
        const seg = { ...raw, size, speed, jump, playerId, wormId, id: trackId };
        for (const k of Object.keys(seg)) if (k.charCodeAt(0) === 95) delete seg[k];
        return seg;
    }

    function isNum(n) {
        return typeof n === "number" && isFinite(n);
    }

    function isSaneSegment(raw, worm, maxSpeed) {
        if (!raw) return false;
        // Every numeric field the engine and the wire rely on must be finite. startTime /
        // endTime / turningVelocity were previously unchecked; a NaN endTime in particular
        // corrupts trail interpolation and (historically) the streamed delta.
        // (Checked one by one, not through an array: this runs for every adopted segment.)
        if (!isNum(raw.duration) || !isNum(raw.startX) || !isNum(raw.startY) || !isNum(raw.endX) || !isNum(raw.endY) ||
            !isNum(raw.startDirection) || !isNum(raw.endDirection) || !isNum(raw.speed) || !isNum(raw.startTime) ||
            !isNum(raw.endTime) || !isNum(raw.turningVelocity)) return false;
        if (!(raw.duration > 0 && raw.duration <= 0.1)) return false;
        // Teleport / speed-hack guard: displacement can't exceed maxSpeed for the duration.
        const dist = Math.hypot(raw.endX - raw.startX, raw.endY - raw.startY);
        if (dist > maxSpeed * raw.duration + worm.radius + 1) return false;
        return true;
    }

    // The client owns its own worm's movement and detects its own crash (local
    // collision against its delayed view of opponents). The server runs collision too,
    // but the two can disagree in dense rounds - so when the client reports its death,
    // apply it authoritatively so the worm the player SEES crash actually dies and the
    // round can end. Killing yourself has no cheat upside, so we trust the report.
    // `claim` (optional): what the client's collision saw, { cause, killerTrackId, cell },
    // used only when the host's own check can't attribute the crash.
    function reportDeath(playerId, wormId, claim) {
        // Live rounds AND the cosmetic victory lap: a lap crash changes no score (the
        // streamed frames say victoryLap, so no client marks anyone out), but killing
        // the worm here emits the worm_died / player_died events that every client's
        // celebration crash sequence and crash sound key off. Without it the winner's
        // celebration kept playing on everyone's screen after they had wiped out.
        const onLap = !!(game && game.gameState.victoryLap);
        if ((!roundActive && !onLap) || !game || !humanIds.has(playerId)) return;
        // Kill the specific worm that crashed (a player can own two - a twin). Fall back
        // to their first alive worm if no wormId was sent (older clients).
        const worm = wormId != null
            ? game.gameState.worms.find((w) => w.id === wormId && w.playerId === playerId)
            : game.gameState.worms.find((w) => w.playerId === playerId && w.alive);
        if (worm && worm.alive) {
            // Best-effort killer attribution (drives the "crashes caused" stat): re-run
            // the fatality check against the worm's latest adopted segment on the host's
            // view. If the host's grid disagrees (dense-round divergence), the death is
            // simply unattributed - the kill itself is still applied as reported.
            let fatal;
            const segs = game.gameState.wormPathSegments[worm.trackId];
            const head = segs && segs[segs.length - 1];
            if (head) fatal = cf.isFatalSegment(game.gameState, worm, head) || undefined;
            // Mark the death as client-called either way (forensics: distinguishes a
            // death the client's own collision reported from a host-side detection),
            // and attach the head-segment snapshot even when the host's grid couldn't
            // attribute the crash.
            if (!fatal) {
                fatal = {via: "client_report", debug: head ? cf.fatalSegmentDebug(worm, head) : undefined};
                // the client's word for what it hit: a wall, or a trail that is on the board
                const gs = game.gameState;
                if (claim && claim.cause === "wall") fatal.cause = "wall";
                else if (claim && claim.cause === "trail" && typeof claim.killerTrackId === "string" && gs.wormPathSegments[claim.killerTrackId]) {
                    fatal.cause = "trail";
                    fatal.killerTrackId = claim.killerTrackId;
                    if (Number.isInteger(claim.cell) && claim.cell >= 0 && gs.playArea && claim.cell < gs.playArea.grid.length) {
                        fatal.debug = { ...(fatal.debug || {}), cell: { index: claim.cell } };
                    }
                }
            } else fatal.via = "client_report";
            cf.killWorm(game.gameState, worm.id, fatal);
        }
    }

    // A human left the match (socket closed). Kill their worm now so the current round
    // can settle, and remember them so they stay dead in every following round instead
    // of respawning as an immortal ghost. Killing your own (departed) worm has no cheat
    // upside and lets the remaining players finish.
    function removePlayer(playerId) {
        if (!humanIds.has(playerId)) return;
        removedPlayers.add(playerId);
        suspendedPlayers.delete(playerId);
        // (a Respawn round: not put back in either)
        if (game) respawn.holdOut(game.gameState, playerId, true);
        if (roundActive && game) {
            const worm = game.gameState.worms.find((w) => w.playerId === playerId && w.alive);
            if (worm) cf.killWorm(game.gameState, worm.id);
        }
    }

    // The player's socket dropped but their seat is parked for reconnection: hand the
    // worm(s) to the sim so they keep driving on their last steering instead of
    // freezing or dying. Core resumes from worm.centerX/direction (kept fresh by
    // applyExternalWormSegment), and the endTime-stamping loop skips non-external
    // worms, so the takeover is seamless mid-track.
    function suspendPlayer(playerId) {
        if (!humanIds.has(playerId) || removedPlayers.has(playerId)) return;
        suspendedPlayers.add(playerId);
        if (game) {
            // The disconnect took the player's keyboard with it, and the keyup for a
            // held steering key never arrives - simulating with that stuck steering
            // sends the worm into a tight death spiral within seconds (usually while
            // the player's reload is still loading). Drive STRAIGHT instead: the
            // least suicidal reading of "the player let go of the keys".
            try { gsf.setPlayerSteering(game.gameState, playerId, 0); } catch { /* player gone */ }
            // a Respawn round puts nobody back in while they are away (a worm nobody
            // steers would crash, come back and crash again)
            respawn.holdOut(game.gameState, playerId, true);
            game.gameState.worms.forEach((w) => {
                if (w.playerId === playerId && w.alive) w.externallyControlled = false;
            });
        }
    }

    // The suspended player reconnected: take the worm back from the sim. The client's
    // stream resumes from the snapshot head (applyClientSegments also re-flips
    // externallyControlled on the first adopted segment, so this is belt-and-braces).
    function resumePlayer(playerId) {
        suspendedPlayers.delete(playerId);
        if (game) {
            game.gameState.worms.forEach((w) => {
                if (w.playerId === playerId && w.alive) w.externallyControlled = true;
            });
            // a Respawn round: back in at once if they have no worm
            if (roundActive) respawn.holdOut(game.gameState, playerId, false);
        }
    }

    // A removed (disconnected) player reconnected: let them spawn again from the next
    // round. Their worm in the CURRENT round stays dead (it was killed at disconnect);
    // this only lifts the stay-dead-every-round mark.
    function restorePlayer(playerId) {
        removedPlayers.delete(playerId);
    }

    // Mid-match state for a reconnecting client: the same shapes the normal stream
    // uses (a game_start-style full gameState, the settled score so far, and the
    // 1-based round number), so the client resumes through its ordinary handlers.
    function snapshot() {
        if (!game) return null;
        return {
            gameState: wireGameState(game.gameState),
            score: match.getCurrentScore(),
            round: match.matchState.roundsData.length + 1,
            setup: lastSetup,
            // Between rounds (the lap): the joiner is looking at a finished round.
            roundOver: !roundActive,
        };
    }

    function stop() {
        stopped = true;
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    }

    // getGameState exposes the live authoritative state (used by the netcode debug
    // harness to render "server truth"; the real server never reads it). Additive,
    // no behaviour change.
    // For the replay (replay/store.js): the finished rounds in replay form, and the
    // index of the round a just-arrived death report belongs to (the live round, or
    // the one whose victory lap is still running).
    function getRoundsData() {
        return match.matchState.roundsData;
    }
    function roundIndex() {
        const finished = match.matchState.roundsData.length;
        return roundActive ? finished : Math.max(0, finished - 1);
    }
    // Whether a round is being played right now (false during the lap between rounds).
    function isRoundActive() { return roundActive; }

    return { setWire, setSteering, applyClientSegments, spawnClientWorm, joinRound, reportDeath, removePlayer, suspendPlayer, resumePlayer, restorePlayer, snapshot, stop, getRoundsData, roundIndex, isRoundActive, getGameState: () => (game ? game.gameState : null) };
}
